import React, { useState, useRef, useEffect } from "react";
import ct from "class-types.macro";
import { UserState } from "~/common/Store/models/RootReadonly";
import { groupingSelected, valid as productValid } from "../../validations";
import { Product } from "~/common/Store/models/ProductListing";
import { valid } from "~/common/Email/EmailValidator";

interface Props {
  product: Product;
  itemNumber: string;
  user: UserState;
}

export default ({ product, itemNumber, user }: Props) => {
  const form = useRef<HTMLFormElement>(null);

  const [hide, setHideElement] = useState(false);

  const [email, setEmail] = useState("");

  useEffect(() => {
    if (user.emailAddress !== undefined) {
      setEmail(String(user.emailAddress));
    }
  }, [user.emailAddress]);

  const [validationMessage, setValidationMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = String(event.target.value);
    setEmail(newEmail);
  };

  const handleSendingEmail = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const isValid = productValid({
      product,
      quantity: 1,
      groupingValue: itemNumber,
      validationsToRun: [groupingSelected],
    });

    if (
      product.grouping &&
      product.groupingAttributes.isVirtual &&
      isValid.isError
    ) {
      return {};
    }

    if (!valid({ emailAddress: email })) {
      return setValidationMessage("Email is invalid, try again.");
    }

    const endpointUrl = "/api/customers/notifications/productavailability/add";

    const request = {
      itemnumber: itemNumber,
      email,
    };

    await fetch(endpointUrl, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(request),
    }).catch((error) => {
      console.log(error);
    });

    setHideElement(true);

    const { emailSignupEvent } = await import("~/gtm");
    emailSignupEvent(email);

    return setSuccessMessage(
      "We will notify you when this product is available.",
    );
  };

  return (
    <div aria-live="polite">
      {!successMessage ? (
        <>
          <div className="price">
            <p className={ct("text-sm", "text-gray-800")}>Out of Stock</p>

            <p className="enter-email" id="enter-email">
              Enter an email to be notified when product is back in stock:
            </p>
          </div>

          <form
            action="#"
            method="post"
            data-target="notifyMe"
            data-testid="notifyMe"
            ref={form}
            onSubmit={handleSendingEmail}
          >
            {!hide && (
              <div className="input-append">
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  aria-describedby={`enter-email ${
                    validationMessage && `error-message`
                  }`}
                  placeholder="Email Address"
                  className={`emailField ${ct(
                    "h-4",
                    "text-xs",
                    "leading-none",
                    "ml-auto",
                    "px-1-1/4",
                    "pb-px",
                    "pt-3/4",
                    "w-24",
                  )}
                ${
                  validationMessage &&
                  ct("border-red-500", "border-solid", "border")
                }`}
                  max-length={100}
                  value={email}
                  onChange={handleEmailChange}
                />
                {
                  // Note: CF uses 'sellout-submit' class to do some DOM manipulation using jquery
                  // To avoid using jquery but to use the react event handling,
                  // updated the classname to 'sellout-submit-react' instead.
                }
                <input
                  type="submit"
                  name="sendButton"
                  className={`btn btn-light btn-mini sellout-submit-react ${ct(
                    "text-xs",
                    "leading-3",
                    "py-1",
                    "mt-0",
                    "mr-0",
                  )} ${
                    validationMessage &&
                    ct("border-red-500", "border-solid", "border")
                  }`}
                  value="Send"
                />
              </div>
            )}
            <div aria-live="polite">
              {validationMessage && (
                <p className={ct("text-xs", "text-red-500")} id="error-message">
                  {validationMessage}
                </p>
              )}
            </div>
          </form>
        </>
      ) : (
        <div className="price">
          <p className={ct("text-sm", "text-gray-800")}>Out of Stock</p>{" "}
          <p className="enter-email">{successMessage}</p>
        </div>
      )}
    </div>
  );
};
